/**
 * `evaluateCondition` — central helper for "should this field render?".
 *
 * Each {@link FieldDef} may carry an optional `condition: (values) => boolean`
 * predicate that decides visibility based on the current form state. The
 * helper is the canonical place that decision is made: callers (the
 * {@link FieldRenderer} for the reactive case, individual field components
 * for the defence-in-depth case) hand the field def + the latest values
 * here and get back a boolean.
 *
 * Why a tiny helper rather than inlining the ternary at every site:
 *
 * - **One place to evolve.** If conditional visibility ever needs to grow
 *   (per-field "hidden" flag from RBAC, async predicates, named expression
 *   strings instead of functions), every callsite already routes through
 *   this single function.
 * - **Symmetric semantics.** Fields without a `condition` predicate are
 *   ALWAYS visible — there's no implicit "off". The helper encodes this
 *   symmetry once instead of having every callsite write
 *   `field.condition ? field.condition(v) : true`.
 * - **Defence in depth.** The renderer applies the predicate reactively
 *   via `useWatch`, but field components also call this helper as a
 *   one-shot check for the case a caller renders a single field directly
 *   (outside the renderer). Same helper, same result.
 *
 * The predicate runs synchronously and must not throw. Throwing predicates
 * would crash the form mid-render; we don't catch here because the render
 * pass is the wrong place to swallow application bugs (silent fallbacks
 * are explicitly banned per spec §9.1). Callers are responsible for
 * authoring side-effect-free predicates.
 */

import type { FieldDef, FormValues } from "./types";

/**
 * Returns `true` when the field should render, `false` when it should be
 * omitted. A field with no `condition` predicate always returns `true`.
 *
 * The signature accepts the loosest values shape (`FormValues =
 * Record<string, unknown>`) because predicates can be composed across
 * forms with different value types. Callers narrow at the predicate body.
 */
export function evaluateCondition(
  field: FieldDef,
  values: FormValues,
): boolean {
  if (!field.condition) return true;
  return field.condition(values);
}
